import {
  CustomerRequestDto,
  useCampaignUpdateControllerCampaignUpdateMutation,
} from '@shared/services/apiService/apiService';
import { ResponseMsg } from '@shared/types/admin/agent';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CreateCampaignFormValue } from '../model/type';
import { removeDuplicatePhones } from '../utils/remove-duplicates-phone';
import { campaignRequestMapper } from '../utils/campaign-mapper';

interface UseEditCampaignProps {
  CustomersListChanged: React.MutableRefObject<boolean>;
  onSaveError?: (error: unknown) => void;
}

interface SaveCampaignOptions {
  includeCustomers?: boolean;
}

export const useEditCampaign = ({ CustomersListChanged, onSaveError }: UseEditCampaignProps) => {
  const [editCampaign] = useCampaignUpdateControllerCampaignUpdateMutation();
  const [responseMsg, setResponseMsg] = useState<ResponseMsg>({ message: '', type: 'success' });
  const [responseLoadingFlag, setResponseLoadingFlag] = useState(false);

  const formMethods = useForm<CreateCampaignFormValue>({
    mode: 'all',
    defaultValues: {
      id: '',
      title: '',
    },
  });

  const handleResponseMessage = (message: string, type: 'success' | 'error') => {
    setResponseMsg({ message, type });
  };

  const processCustomersList = useCallback((data: CreateCampaignFormValue) => {
    const customers = data.customers?.filter((item) => item.id);

    const uniqueCustomers = removeDuplicatePhones(customers || []);

    return uniqueCustomers as CustomerRequestDto[] | undefined;
  }, []);

  const saveCampaignForm = useCallback(
    async (data: CreateCampaignFormValue, options: SaveCampaignOptions = {}): Promise<boolean> => {
      setResponseLoadingFlag(true);
      setResponseMsg({ message: '', type: 'success' });

      try {
        const includeCustomers = options.includeCustomers ?? CustomersListChanged.current;
        const customers = includeCustomers ? processCustomersList(data) : undefined;
        const campaignUpdateRequestDto = campaignRequestMapper(data, customers);

        await editCampaign({ campaignUpdateRequestDto, id: data.id }).unwrap();
        handleResponseMessage('adminMainPage.campaigns.successfullyEdited', 'success');
        return true;
      } catch (error) {
        handleResponseMessage('adminMainPage.campaigns.somethingWentWrong', 'error');
        onSaveError?.(error);
        return false;
      } finally {
        setResponseLoadingFlag(false);
      }
    },
    [CustomersListChanged, editCampaign, onSaveError, processCustomersList],
  );

  const onSubmit = formMethods.handleSubmit(async (data) => saveCampaignForm(data));

  return {
    onSubmit,
    saveCampaignForm,
    setResponseMsg,
    responseMsg,
    responseLoadingFlag,
    formMethods,
  };
};
